import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTenantInvitation,
  createTenantJoinCode,
  deactivateTenantMembership,
  getTenantOnboardingAccess,
  reactivateTenantMembership,
  refreshTenantInvitation,
  revokeTenantInvitation,
  revokeTenantJoinCode,
} from '../lib/tenantOnboarding';
import type {
  TenantInvitationCreatePayload,
  TenantInvitationRefreshPayload,
  TenantJoinCodeCreatePayload,
  TenantOnboardingAccessResponse,
} from '../types';
import { tenantDirectoryKeys } from './useTenantDirectory';

export const tenantOnboardingKeys = {
  all: ['tenant-onboarding'] as const,
  access: () => [...tenantOnboardingKeys.all, 'access'] as const,
};

export function useTenantOnboardingAccess(enabled: boolean = true) {
  const queryClient = useQueryClient();

  const query = useQuery<TenantOnboardingAccessResponse>({
    queryKey: tenantOnboardingKeys.access(),
    queryFn: getTenantOnboardingAccess,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });

  const refreshAccess = () => {
    void queryClient.invalidateQueries({ queryKey: tenantOnboardingKeys.access() });
    void queryClient.invalidateQueries({ queryKey: tenantDirectoryKeys.overview() });
  };

  const createJoinCode = useMutation({
    mutationFn: (payload: TenantJoinCodeCreatePayload) => createTenantJoinCode(payload),
    onSuccess: () => {
      refreshAccess();
    },
  });

  const revokeJoinCode = useMutation({
    mutationFn: (joinCodeId: number) => revokeTenantJoinCode(joinCodeId),
    onSuccess: () => {
      refreshAccess();
    },
  });

  const createInvitation = useMutation({
    mutationFn: (payload: TenantInvitationCreatePayload) => createTenantInvitation(payload),
    onSuccess: () => {
      refreshAccess();
    },
  });

  const revokeInvitation = useMutation({
    mutationFn: (invitationId: number) => revokeTenantInvitation(invitationId),
    onSuccess: () => {
      refreshAccess();
    },
  });

  const refreshInvitation = useMutation({
    mutationFn: ({ invitationId, payload }: { invitationId: number; payload?: TenantInvitationRefreshPayload }) =>
      refreshTenantInvitation(invitationId, payload),
    onSuccess: () => {
      refreshAccess();
    },
  });

  const deactivateMembership = useMutation({
    mutationFn: (membershipId: number) => deactivateTenantMembership(membershipId),
    onSuccess: () => {
      refreshAccess();
    },
  });

  const reactivateMembership = useMutation({
    mutationFn: (membershipId: number) => reactivateTenantMembership(membershipId),
    onSuccess: () => {
      refreshAccess();
    },
  });

  return {
    access: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    createJoinCode: createJoinCode.mutateAsync,
    revokeJoinCode: revokeJoinCode.mutateAsync,
    createInvitation: createInvitation.mutateAsync,
    revokeInvitation: revokeInvitation.mutateAsync,
    refreshInvitation: refreshInvitation.mutateAsync,
    deactivateMembership: deactivateMembership.mutateAsync,
    reactivateMembership: reactivateMembership.mutateAsync,
    isCreatingJoinCode: createJoinCode.isPending,
    isRevokingJoinCode: revokeJoinCode.isPending,
    isCreatingInvitation: createInvitation.isPending,
    isRevokingInvitation: revokeInvitation.isPending,
    isRefreshingInvitation: refreshInvitation.isPending,
    isDeactivatingMembership: deactivateMembership.isPending,
    isReactivatingMembership: reactivateMembership.isPending,
  };
}
